// Centralized authentication provider. Wraps Supabase auth helpers and keeps the
// current user/session in React state so the rest of the app can subscribe via
// useAuth without re-implementing listeners.
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  ReactNode,
} from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { setSentryUser, clearSentryUser } from '../lib/sentry';
import { identifyUser, resetAnalyticsUser } from '../lib/analytics';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<{ user: User | null; session: Session | null }>;
  signIn: (email: string, password: string) => Promise<{ user: User | null; session: Session | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let mounted = true;
    let initialized = false;

    (async () => {
      // Initial session fetch ensures SSR-like hydration so routes know whether
      // to gate content immediately after page load.
      try {
        const { data, error } = await supabase.auth.getSession();
        if (!mounted) return;

        if (error) {
          // Session error handled silently
        } else {
          setSession(data.session ?? null);
          setUser(data.session?.user ?? null);
          
          // Set Sentry user context if logged in
          if (data.session?.user) {
            setSentryUser({
              id: data.session.user.id,
              email: data.session.user.email,
            });
            identifyUser(data.session.user.id, { email: data.session.user.email });
          }
        }
      } catch {
        // Error handled silently
      } finally {
        if (mounted && !initialized) {
          initialized = true;
          setLoading(false);
        }
      }
    })();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (!mounted) return;

      // Keep the session in sync with Supabase auth so consumers re-render when
      // the user logs in/out or the token refreshes.
      setSession(newSession);
      setUser(newSession?.user ?? null);

      // Update Sentry user context
      if (newSession?.user) {
        setSentryUser({
          id: newSession.user.id,
          email: newSession.user.email,
        });
        identifyUser(newSession.user.id, { email: newSession.user.email });
      } else {
        clearSentryUser();
        resetAnalyticsUser();
      }

      if (!initialized) {
        initialized = true;
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthContextType>(() => ({
    user,
    session,
    loading,
    signUp: async (email, password) => {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth`,
          // Use implicit flow instead of PKCE to avoid code verifier issues
          data: {
            // Additional user metadata can go here
          }
        },
      });
      if (error) {
        throw error;
      }
      // Update state immediately with new session
      if (data.session) {
        setSession(data.session);
        setUser(data.session.user);
      }
      return { user: data.user, session: data.session };
    },
    signIn: async (email, password) => {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        throw error;
      }
      if (data.session) {
        setSession(data.session);
        setUser(data.session.user);
      }
      return { user: data.user, session: data.session };
    },
    signOut: async () => {
      const { error } = await supabase.auth.signOut();
      if (error) {
        throw error;
      }
      setSession(null);
      setUser(null);
    },
  }), [user, session, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
